import Vue from 'vue';
import ICog from 'src/assets/cog.svg';
import { Prop, Component, Watch, Inject } from 'vue-property-decorator';
import type { EntityOfType } from '@cognitoforms/model.js';
import type { FormEntry } from '@cognitoforms/types/server-types/forms/model/form-entry';
import { getEntryId } from 'src/web-api/entry-views/entry-view-util';
import type LookupManager from 'src/framework/model/extensions/lookup-field/lookup-manager';
import type { FormEventScope } from 'src/framework/eventing/form-event-scope';
import { FormEvents } from 'src/framework/public/events';
import { removeExtraSpace } from 'src/util/helpers';
import { EntryStatus } from './EntryStatus';
import { getAvatarSVG } from '@cognitoforms/utils/avatar';
import type FileService from 'src/web-api/file-service';

type ChoiceOption = {
	value: string;
	displayValue: string;
	image?: string;
	svg?: string;
};

@Component({
	name: 'CLookup',
	components: { ICog },
	model: {
		prop: 'value',
		event: 'change'
	}
})
export default class Lookup extends Vue {
	@Prop()
	value: EntityOfType<FormEntry> | EntityOfType<FormEntry>[];

	@Prop()
	manager: LookupManager;

	@Prop({ default: false })
	readonly: boolean;

	@Inject('formEvents')
	formEvents: FormEventScope;

	@Prop()
	entryStatus: EntryStatus;

	@Prop()
	fileService: FileService;

	placeholderFocused = false;
	initializing = true;
	slot = { id: null, ids: [] };
	forgetInitialValue = false;
	missing = [];
	initialValue: EntityOfType<FormEntry> | EntityOfType<FormEntry>[];
	private cleanupTasks: (() => void)[] = [];
	private loadDataTask: Promise<EntityOfType<FormEntry> | EntityOfType<FormEntry>[]>;

	async created() {
		if (this.formEvents) {
			const resetEntryCallback = () => {
				this.initialValue = this.value;
				this.forgetInitialValue = false;
				const unwatch = this.$watch('manager.filteredIndexes', () => {
					// after the filteredIndexes change once, allow the initial value to be forgotten on subsequent changes
					this.forgetInitialValue = true;
					unwatch();
				});

				this.focusLookup();

				if (!this.initializing) {
					if (this.manager.initializing) {
						return this.manager.ready;
					}
					else
						return this.manager.initialize();
				}
			};
			this.formEvents.on(FormEvents.ResetEntry, resetEntryCallback);
			this.cleanupTasks.push(() => this.formEvents.off(FormEvents.ResetEntry, resetEntryCallback));
		}

		this.initialValue = this.value;

		await this.manager.ready;
		// allow computed/watchers to run before setting loading to false
		await this.$nextTick();
		this.initializing = false;
		this.forgetInitialValue = true;
		this.focusLookup();
	}

	beforeDestroy() {
		this.cleanupTasks.forEach(task => task());
	}

	// Updates focus after lookup loads
	focusLookup() {
		// Try to get the input and set it to focus after loading
		this.$nextTick(() => {
			// Base case in recursion
			if (this.$el.querySelector('input, select')) {
				if (this.placeholderFocused) {
					this.placeholderFocused = false;
					(this.$el.querySelector('input, select') as HTMLElement).focus();
				}
			}
			else if (!this.readonly)
				return setTimeout(() => this.focusLookup(), 100);
		});
	}

	handleBlur() {
		if (this.initializing)
			this.placeholderFocused = false;
	}

	handleFocus() {
		this.placeholderFocused = true;
	}

	get isList() {
		return Array.isArray(this.value);
	}

	get valueAsOptions() {
		return this.createOptionsFromEntries(this.value);
	}

	createOptionsFromEntries(entries: EntityOfType<FormEntry> | EntityOfType<FormEntry>[]): ChoiceOption[] {
		return (Array.isArray(entries) ? entries : [entries])
			.filter(entry => !!entry)
			.map((entry: EntityOfType<FormEntry>) => {
				const displayValue = removeExtraSpace(entry.toString(this.manager.summaryFormat));
				const option = { displayValue: displayValue, value: entry.Id } as ChoiceOption;

				const index = this.manager.filteredIndexes.find(o => o.Id.split('|').at(2) === entry.Id);
				const imageValue = index?.AdditionalValues.find(v => v.FieldId === 'Image');
				if (imageValue) {
					if (imageValue.Value.length <= 2) {
						option.svg = getAvatarSVG(index.Summary, imageValue.Value.at(0) ?? '', imageValue.Value.at(1) ?? '', false, 'cog-');
					}
					else {
						option.image = this.fileService.getThumbnailUrl({
							Id: imageValue.Value,
							Name: '',
							ContentType: '',
							Size: 0,
							IsEncrypted: false,
							StorageUrl: ''
						});
					}
				}

				return option;
			});
	}

	@Watch('manager.filteredIndexes')
	filterChanged() {
		if (this.forgetInitialValue)
			this.initialValue = null;
	}

	get choiceOptions(): ChoiceOption[] {
		// in readonly mode, just derive the "options" from the form entry, since only the selected options are relevant for readonly
		if (this.readonly)
			return this.valueAsOptions;
		else {
			const optionsFromIndex = this.manager.filteredIndexes.map(o => {
				const option = {
					displayValue: removeExtraSpace((o.Summary + (this.readonly ? '' : ` ${o.Description}`))),
					value: getEntryId(o)
				} as ChoiceOption;

				const imageValue = o?.AdditionalValues.find(v => v.FieldId === 'Image');
				if (imageValue) {
					// If the value has a length of <= 2, we've got the initials of the person and they don't have an uploaded avatar
					// Generate the avatar if so.
					if (imageValue.Value.length <= 2) {
						option.svg = getAvatarSVG(o.Summary, imageValue.Value.at(0) ?? '', imageValue.Value.at(1) ?? '', false, 'cog-');
					}
					else {
						option.image = this.fileService.getThumbnailUrl({
							Id: imageValue.Value,
							Name: '',
							ContentType: '',
							Size: 0,
							IsEncrypted: false,
							StorageUrl: ''
						});
					}
				}
				return option;
			});

			// if we're allowing the lookup to have a value that isn't in the index, ensure it has a corresponding choice option
			// The resulting array may not be in the correct order when editing
			if (this.manager.allowUnknownEntry) {
				const optionsValueSet = new Set(optionsFromIndex.map(o => o.value));
				this.missing = this.createOptionsFromEntries(this.initialValue)
					.filter(o => !optionsValueSet.has(o.value));
				return this.missing.concat(optionsFromIndex);
			}
			else
				return optionsFromIndex;
		}
	}

	private updateCounter = 0;
	@Watch('value', { immediate: true })
	async valueUpdated(value: EntityOfType<FormEntry> | EntityOfType<FormEntry>[]) {
		const updateCounter = ++this.updateCounter;
		// don't push the value change to the slot until the manager is initialized to ensure the options list is populated with
		// an option which corresponds to the lookup value
		if (this.manager.initializing)
			await this.manager.ready;

		if (this.updateCounter !== updateCounter)
			return;

		const optionIds = new Set(this.choiceOptions.map(o => o.value));
		if (this.isList) {
			const formEntryIds = (value as EntityOfType<FormEntry>[]).map(e => e.Id);
			if (this.initializing || this.manager.allowUnknownEntry)
				this.slot.ids = formEntryIds;
			else
				this.slot.ids = formEntryIds.filter(id => optionIds.has(id));
		}
		else if (value && 'Id' in value && (this.initializing || this.manager.allowUnknownEntry || optionIds.has(value.Id)))
			this.slot.id = (value as EntityOfType<FormEntry>).Id;
		else
			this.slot.id = null;
	}

	@Watch('slot.id')
	async valueUpdatedByUser(id) {
		if (this.isList)
			return;

		// Exit if the form is currently rendering (due to entry changing on the entries page)
		if (this.entryStatus === EntryStatus.Pending)
			return;

		if (!id) {
			this.loadDataTask = null;
			this.$emit('change', null);
		}
		else if (!this.value || (this.value as FormEntry).Id !== id) {
			const ignoreFilter = this.missing.some(o => o.value === id);
			const loadDataTask = this.loadDataTask = this.manager.getEntryById(id, ignoreFilter);
			const newValue = await loadDataTask;
			if (loadDataTask === this.loadDataTask)
				this.$emit('change', newValue);
		}
	}

	@Watch('slot.ids')
	async listValueUpdatedByUser(ids: string[]) {
		if (!this.isList)
			return;

		// Exit if the form is currently rendering (due to entry changing on the entries page)
		if (this.entryStatus === EntryStatus.Pending)
			return;

		const currentIds = ((this.value || []) as FormEntry[]).map(e => e.Id);
		if (this.isList && (ids.length !== currentIds.length || !ids.every(id => currentIds.includes(id)))) {
			const ignoreFilterFor = (id: string) => this.missing.some(o => o.value === id);
			const loadDataTask = this.loadDataTask = Promise.all(ids.map(id => this.manager.getEntryById(id, ignoreFilterFor(id))));
			const newValue = await loadDataTask;
			if (loadDataTask === this.loadDataTask)
				this.$emit('change', newValue);
		}
	}
};